<script setup>
import { computed } from 'vue';

const props = defineProps({
  gradeTitle: { 
    type: String,
    required: true,
  },
  classNum: { 
    type: String,
    required: true,
  },
  chartType: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  }, 
  levelData: {
    type: Array,
    required: true,
    validator: (value) => value.every(item => item.level && typeof item.percentage === 'number' && typeof item.count === 'number'),
  },
  maxScale: { 
    type: Number,
    default: 100,
  },
  chartHeight: { 
    type: String,
    default: '250px',
  }
});

// X축 그룹 라벨이 차지할 예상 높이 (CSS와 연동)
const GROUP_LABEL_AREA_HEIGHT = '2rem'; 

// === 색상 정의 ===
const LITERACY_COLOR = '#6484F080'; 
const NUMERACY_COLOR = '#F8808280';

// 차트 유형에 따른 기본 막대 색상 결정
const barColor = computed(() => {
  return props.chartType === 'literacy' ? LITERACY_COLOR : NUMERACY_COLOR;
});

// 막대 높이 계산 
const getBarHeight = (value) => {
  const numericValue = Number(value); 
  if (value === null || value === undefined || value === '' || isNaN(numericValue)) {
    return '0%';
  }
  
  // 100% 기준으로 막대 높이 계산 (데이터 자체가 백분율)
  return Math.max(0, numericValue) + '%';
  //return 100 + '%';
};

// 화면에 표시될 값 (숫자 또는 'N') 반환
const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return 'N';
  }
  return value;
};

// 10% 단위 그리드 라인 위치 계산
const gridLines = computed(() => {
  const lines = [];
  for (let i = 1; i < 11; i++) { // 10%부터 100%까지
    lines.push(i * 10);
  }
  return lines;
});
</script>

<template>
  <div class="level-chart-wrapper">
    <div class="level-chart-container" :style="{height: `calc(${chartHeight} + ${GROUP_LABEL_AREA_HEIGHT})`}">
      <!-- Y축 라벨 -->
      <div class="chart-y-axis" :style="{height: chartHeight}">        
        <div class="y-axis-label" v-for="n in 11" :key="n" :style="{ bottom: ((n - 1) * 10) + '%' }">
          {{ (n - 1) * 10 }}%
        </div>
      </div>
      <div class="chart-bars-wrap">
        <!-- 그리드 라인 -->
        <div class="chart-grid-lines" :style="{height: chartHeight}">
          <p
            v-for="pos in gridLines"
            :key="pos"
            class="grid-line"
            :style="{ bottom: pos + '%' }"
          ></p>
        </div>
        <div v-for="(item, index) in levelData" :key="index" class="bar-item-group"> 
            <div class="bar-item" :style="{height: chartHeight}"> 
                <div class="bar" :style="{ height: getBarHeight(item.percentage), backgroundColor: barColor }">
                    <div class="bar-top-value">
                        <p>{{ getDisplayValue(item.percentage) }}{{ getDisplayValue(item.percentage) === 'N%' ? '' : '%' }}</p>    
                        <p>({{ getDisplayValue(item.count) }}{{ getDisplayValue(item.count) === 'N명' ? '' : '명' }})</p>             
                    </div>
                </div>
            </div>
            <div class="group-label">{{ item.level }}</div>
        </div>
      </div>
    </div>   
    <p class="chart-type" :class="props.chartType">
        <template v-if="props.chartType === 'literacy'">문해력</template>
        <template v-else-if="props.chartType === 'numeracy'">수리력</template>
    </p>
  </div>
</template>

<style lang="scss" scoped>
.level-chart-wrapper {
  padding: 60px 30px 20px 16px;
  border: 1px solid #999;
  border-radius: 10px;
  overflow: visible;
}
.level-chart-container {
    display: flex;
    gap: 20px;
    width: 100%;
    position: relative;
    align-items: flex-start;
    margin-top: 40px;
}
.chart-y-axis {
    position: relative;
    width: 40px; 
    z-index: 2;
    font-size: 14px;      
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    line-height: 10%;
    text-align: right;
}
.chart-bars-wrap {
  display: flex;
  flex: 1;
  justify-content: space-around;
  position: relative;
}

// 그리드
.chart-grid-lines {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
}  
.grid-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border-bottom: 2px dashed #e0e0e0;
}

.bar-item-group {
    display: flex;
    flex-direction: column; 
    align-items: center;
    position: relative;  
    width: 100px;   
}
.bar-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding: 0 20px;    
    position: relative;
    height: 100%; 
    border-bottom: 2px solid #000;
}
.bar {
  width: 20px; 
  border-radius: 4px 4px 0 0;
  transition: height 0.5s ease-out;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  min-height: 3px;

  .bar-top-value {
    position: absolute;
    top: -60px;
    font-size: 18px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
  }
}
.group-label {
    font-size: 18px;
    font-weight: 500;
    padding: 5px 0;
}
.chart-type { 
    border-radius: 5px;
    text-align: center;
    line-height: 28px;
    font-size: 16px;
    font-weight: 500;
    margin-left: 60px;
    &.literacy {
        border: 1px solid #4362CC;
        color: #4362CC;
    }
    &.numeracy {
        border: 1px solid #CE4E56;
        color: #CE4E56;
    }
}
</style>